/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import { Field, Formik } from "formik";
import { Input, Button, Select, Textarea } from "components";
import * as Yup from "yup";
import { VALIDATION_MESSAGES, ROUTE_ENUM } from "enums";
import {
  SingleSectionElement,
  Drawing,
  HoleDimension,
  JoineryDimension,
  AdditionBottomWindow,
  AdditionSideWindow,
  WindowAccessories,
} from "features/addingMeasurement";
import { handleMeasurementSuccess } from "features/addingMeasurement/utils";
import { useGetOrderDetails } from "hooks/orders";
import { usePutMeasurementInfo, useStartMeasurement } from "hooks/measurements";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useUploadFile } from "hooks/utils";
import { ScrollToError } from "utils";

interface WindowsInRowFormMeasurementValuesType {
  measurementUuid: string | null;
  prevStep: () => void;
  measurementInfo: any;
  setStep(step: number): void;
}

const WindowsInRowFormMeasurementValues = ({
  measurementUuid,
  prevStep,
  setStep,
  measurementInfo,
}: WindowsInRowFormMeasurementValuesType) => {
  const navigate = useNavigate();
  const [isErrorDimension, setIsErrorDimension] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderUuid = searchParams.get("orderUuid");
  const page = searchParams.get("page");
  const initialMeasurementInfo = measurementInfo?.measurementInfo;
  const [formDraw, setFormDraw] = useState<any>();
  const [isAddNew, setIsAddNew] = useState(false);
  useGetOrderDetails(orderUuid, true);

  const goToList = () => {
    navigate(`${ROUTE_ENUM.ACTIVE_ORDER}?orderUuid=${orderUuid}&page=${page}`);
  };

  const commentsWindowAdditionComment =
    initialMeasurementInfo?.windowAdditionUuids?.find(
      (item: any) => item.comment
    )?.comment;

  const startMeasurement = useStartMeasurement({
    onSuccess: (data) => {
      handleMeasurementSuccess(data, searchParams, setSearchParams, setStep);
    },
  });

  const uploadFileMutation = useUploadFile({
    onSuccess: (data) => {
      sessionStorage.removeItem("canvasState");
      if (isAddNew) {
        setIsAddNew(false);
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        goToList();
      }
    },
  });

  const { mutate: updateMeasurementInfo } = usePutMeasurementInfo({
    onSuccess: (data) => {
      if (formDraw !== "oldDraw") {
        uploadFileMutation.mutate({
          parentUuid: data.measurement.measurementInfo.uuid,
          type: "windowDivisionDrawing",
          files: [formDraw],
        });
      } else {
        goToList();
      }
    },
  });

  const handleOnSubmit = async (values: any, actions: any) => {
    setFormDraw(values.drawing);
    updateMeasurementInfo(values);
    if (isAddNew) {
      startMeasurement.mutate({
        typeOfMeasurementUuid: measurementInfo.typeOfMeasurementUuid,
        orderUuid,
      });
    }
  };

  const handleCheckValidateDimensions = (
    values: any,
    setFieldError: any,
    errors: any,
    setFieldValue: any
  ) => {
    const {
      joinerySizeWidth,
      windowSideAdditionSize,
      joinerySizeHeight,
      windowBottomAdditionSize,
      holeSizeHeight,
      holeSizeWidth,
      warningComment,
    } = values;

    if (
      joinerySizeHeight &&
      joinerySizeWidth &&
      holeSizeHeight &&
      holeSizeWidth
    ) {
      const holeWidth = parseInt(holeSizeWidth, 10);
      const holeHeight = parseInt(holeSizeHeight, 10);
      const joineryHeight = parseInt(joinerySizeHeight, 10);
      const joineryWidth = parseInt(joinerySizeWidth, 10);
      const bottomAddition = parseInt(windowBottomAdditionSize || "0", 10);
      const sideAddition = parseInt(windowSideAdditionSize || "0", 10);

      const widthDifference = holeWidth - (joineryWidth + sideAddition);
      const heightDifference = holeHeight - (joineryHeight + bottomAddition);
      if (
        widthDifference >= 30 &&
        widthDifference <= 50 &&
        heightDifference >= 30 &&
        heightDifference <= 50
      ) {
        setIsErrorDimension(false);
      } else {
        if (!errors.warningComment && !warningComment) {
          setFieldError("warningComment", VALIDATION_MESSAGES.REQUIRED);
        }
        setIsErrorDimension(true);
      }
    }
    return false;
  };

  const initialValues = {
    measurementUuid: measurementUuid,
    roomName: initialMeasurementInfo?.roomName || "",
    holeSizeWidth: initialMeasurementInfo?.holeSizeWidth || "",
    holeSizeHeight: initialMeasurementInfo?.holeSizeHeight || "",
    joinerySizeWidth: initialMeasurementInfo?.joinerySizeWidth || "",
    joinerySizeHeight: initialMeasurementInfo?.joinerySizeHeight || "",
    windowBottomAddition: initialMeasurementInfo?.windowBottomAddition || "",
    windowBottomAdditionSize:
      initialMeasurementInfo?.windowBottomAdditionSize || "",
    windowSideAdditionSize:
      initialMeasurementInfo?.windowSideAdditionSize || "",
    windowSideAddition: initialMeasurementInfo?.windowSideAddition || "",
    windowAdditionUuids: initialMeasurementInfo?.windowAdditionUuids || [],
    typeOfSlidingWindow: initialMeasurementInfo?.typeOfSlidingWindow || "",
    comment: initialMeasurementInfo?.comment || "",
    windowComment: initialMeasurementInfo?.windowComment || "",
    commentsWindowAddition: commentsWindowAdditionComment || "",
    warningComment: initialMeasurementInfo?.warningComment || "",
    file: initialMeasurementInfo?.file || [],
    windowBottomAdditionComment:
      initialMeasurementInfo?.windowBottomAdditionComment || "",
    windowSideAdditionComment:
      initialMeasurementInfo?.windowSideAdditionComment || "",
  };

  interface ObjectWithComment {
    comment: string;
  }

  const isObjectWithComment = (value: any): value is ObjectWithComment => {
    return (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      typeof value.comment === "string" &&
      value.comment.trim() !== ""
    );
  };

  const validationSchema = Yup.object().shape({
    holeSizeWidth: Yup.number().required(VALIDATION_MESSAGES.REQUIRED),
    holeSizeHeight: Yup.number().required(VALIDATION_MESSAGES.REQUIRED),
    roomName: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
    joinerySizeWidth: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
    joinerySizeHeight: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
    windowBottomAdditionSize: Yup.string().test(
      "windowBottomAdditionSize-required",
      VALIDATION_MESSAGES.REQUIRED,
      function (value) {
        const { windowBottomAddition } = this.parent;
        if (!windowBottomAddition && value) {
          return this.createError({
            message: VALIDATION_MESSAGES.REQUIRED,
          });
        }
        if (windowBottomAddition) {
          return value !== undefined && value !== "";
        }
        return true;
      }
    ),
    windowAdditionUuids: Yup.array()
      .of(
        Yup.mixed().test(
          "is-string-or-object-with-comment",
          VALIDATION_MESSAGES.REQUIRED,
          (value) =>
            typeof value === "string" ||
            (typeof value === "object" &&
              value !== null &&
              !Array.isArray(value) &&
              typeof (value as ObjectWithComment).comment === "string" &&
              (value as ObjectWithComment).comment.trim() !== "")
        )
      )
      .required(VALIDATION_MESSAGES.REQUIRED)
      .test(
        "has-at-least-one-loose-string-or-object-with-comment",
        VALIDATION_MESSAGES.REQUIRED,
        (array) =>
          array.some(
            (item) => typeof item === "string" || isObjectWithComment(item)
          )
      ),
    windowSideAdditionSize: Yup.string().test(
      "windowSideAdditionSize-required",
      VALIDATION_MESSAGES.REQUIRED,
      function (value) {
        const { windowSideAddition } = this.parent;
        if (!windowSideAddition && value) {
          return this.createError({
            message: VALIDATION_MESSAGES.REQUIRED,
          });
        }
        if (windowSideAddition) {
          return value !== undefined && value !== "";
        }
        return true;
      }
    ),
    drawing: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
    warningComment: isErrorDimension
      ? Yup.string().required(VALIDATION_MESSAGES.REQUIRED)
      : Yup.string(),
  });

  return (
    <div>
      <div className="am-heading">Wartości pomiaru</div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={isValidated}
        onSubmit={async (value, actions) => handleOnSubmit(value, actions)}
      >
        {({
          handleSubmit,
          errors,
          values,
          submitCount,
          touched,
          validateField,
          setFieldError,
          setFieldValue,
        }) => {
          if (submitCount > 0) {
            setIsValidated(true);
          }
          handleCheckValidateDimensions(
            values,
            setFieldError,
            errors,
            setFieldValue
          );

          return (
            <form className="amfs__form form" onSubmit={handleSubmit}>
              <ScrollToError errorDimension={isErrorDimension} />
              <Field
                errors={errors}
                label="Rysunek podziału okien"
                name="drawing"
                component={Drawing}
              />
              <SingleSectionElement
                touched={touched}
                name="roomName"
                errors={errors}
              >
                <Field
                  label="Nazwa pomieszczenia"
                  name="roomName"
                  maxLength={30}
                  component={Input}
                />
              </SingleSectionElement>
              <Field
                errors={errors}
                validateField={validateField}
                label="Wymiar otworu"
                component={HoleDimension}
              />
              <Field
                errors={errors}
                label="Wymiar stolarki"
                validateField={validateField}
                component={JoineryDimension}
              />
              <Field
                errors={errors}
                label="Dodatek dół okna"
                name="windowBottomAddition"
                component={AdditionBottomWindow}
                validateField={validateField}
                setFieldValue={setFieldValue}
                setFieldError={setFieldError}
                white
              />
              <Field
                errors={errors}
                validateField={validateField}
                label="Dodatek bok okna"
                name="windowBottomAddition"
                component={AdditionSideWindow}
                setFieldValue={setFieldValue}
                setFieldError={setFieldError}
                isErrorDimension={isErrorDimension}
                white
              />
              <Field
                values={values}
                errors={errors}
                validateField={validateField}
                name="windowAdditionUuids"
                component={WindowAccessories}
              />
              <SingleSectionElement
                className="am-margin-top"
                touched={touched}
                name="typeOfSlidingWindow"
                errors={errors}
              >
                <Field
                  label="Rodzaj okna przesuwnego"
                  name="typeOfSlidingWindow"
                  placeholder="Wybierz"
                  component={Select}
                  options={[
                    { value: "hst", label: "HST" },
                    { value: "smartSlide", label: "Smart-slide" },
                    { value: "psk", label: "PSK" },
                  ]}
                  white
                  optional
                />
              </SingleSectionElement>
              <Field
                className="am-margin-top"
                errors={errors}
                label="Dodatkowe uwagi"
                optional
                name="comment"
                component={Textarea}
                maxLength={250}
              />
              <div className="am__buttons am__buttons--last-step">
                <Button
                  stroke
                  onClick={() => prevStep()}
                  label="Poprzedni krok"
                />
                <Button
                  stroke
                  onClick={async () => {
                    await handleSubmit();
                    setIsAddNew(true);
                  }}
                  label="Zakończ i dodaj kolejny tego typu"
                />
                <Button
                  onClick={() => {
                    setIsAddNew(false);
                    handleSubmit();
                  }}
                  label="Zakończ pomiar"
                />
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default WindowsInRowFormMeasurementValues;
